import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { RECOVERY_HASH_TOKENS, supabase } from '../lib/supabaseClient'
import { TermsPrivacyFooter } from '../components/TermsPrivacyFooter'

/**
 * Landing page for the Supabase password-reset email link.
 *
 * Supabase redirects here with recovery tokens in the URL hash fragment.
 * The SDK's auto-detect (detectSessionInUrl: true) tries to establish a
 * session on init, but can silently fail if _getUser() encounters a
 * transient error before _saveSession completes (see RECOVERY_HASH_TOKENS
 * in supabaseClient.ts). The ensureSession effect below is the
 * belt-and-suspenders path: explicit setSession() retry on mount and once
 * more at submit time so updateUser() always has a valid session.
 *
 * AuthContext deliberately does NOT hydrate session state on this route
 * (guarded in both getSession init and onAuthStateChange) so the recovery
 * token never silently authenticates the user as if they had logged in.
 */
export function UpdatePasswordPage() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const navigate = useNavigate()
  const sessionEnsuredRef = useRef(false)

  useEffect(() => {
    let cancelled = false

    async function ensureSession() {
      if (sessionEnsuredRef.current) return
      sessionEnsuredRef.current = true

      const { data } = await supabase.auth.getSession()
      if (cancelled || data.session) return

      // SDK auto-detect failed — retry with the hash-token snapshot.
      if (!RECOVERY_HASH_TOKENS) return

      await supabase.auth.setSession(RECOVERY_HASH_TOKENS)
    }

    ensureSession()
    return () => { cancelled = true }
  }, [])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)

    // Defense-in-depth: one more setSession attempt at submit time in case
    // the mount effect's retry hasn't completed yet.
    const { data: pre } = await supabase.auth.getSession()
    if (!pre.session && RECOVERY_HASH_TOKENS) {
      await supabase.auth.setSession(RECOVERY_HASH_TOKENS)
    }

    const { error: err } = await supabase.auth.updateUser({ password })
    setLoading(false)

    if (err) {
      setError(
        'Could not update your password. The reset link may have expired — please request a new one.',
      )
      return
    }

    setDone(true)
    // Sign out the recovery session so the user lands on a clean login form.
    await supabase.auth.signOut()
    setTimeout(() => navigate('/configure', { replace: true }), 2000)
  }

  return (
    <div className="min-h-screen bg-brand-bg text-brand-charcoal font-sans grid place-items-center px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-2">
          <div className="h-10 w-10 rounded-xl grid place-items-center bg-brand-orange text-white text-sm font-bold">
            C
          </div>
          <span className="text-sm font-semibold tracking-wider text-brand-charcoal">
            CHRONA{' '}
            <span className="text-[10px] font-bold tracking-widest px-1.5 py-0.5 rounded text-brand-orange bg-brand-orange/10">
              PRO
            </span>
          </span>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5 shadow-sm">
          <h1 className="text-base font-semibold text-center">Set a new password</h1>

          {done ? (
            <div className="text-sm text-center text-slate-600 space-y-1">
              <p className="font-medium text-green-700">Password updated.</p>
              <p className="text-slate-500">Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="upw-password" className="block text-xs font-medium text-slate-600">
                  New password
                </label>
                <input
                  id="upw-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="upw-confirm" className="block text-xs font-medium text-slate-600">
                  Confirm password
                </label>
                <input
                  id="upw-confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/40 focus:border-brand-orange"
                />
              </div>

              {error && (
                <p className="text-xs text-red-600 rounded-md bg-red-50 border border-red-200 px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-brand-orange text-white py-2.5 text-sm font-medium hover:bg-brand-orange/90 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                Update password
              </button>
            </form>
          )}

          <p className="text-xs text-center text-slate-500">
            <Link to="/configure" className="text-brand-orange hover:underline">
              ← Back to sign in
            </Link>
          </p>
        </div>
      </div>
      <div className="mt-8">
        <TermsPrivacyFooter />
      </div>
    </div>
  )
}
